import { useMemo, useEffect } from "react";
import { Spin } from "antd";
import { Trans } from "react-i18next";
import { getUserAssetsV2, moveToTrash, type UserAssetV2Record } from "@apis/http/api";
import type { UseImageBatchSelectionReturn } from "../hooks/useImageBatchSelection";
import {
  PathsApiUserAssetsGetParametersQueryAssetType,
  PathsApiUserAssetsGetParametersQueryAssetSource,
} from "@apis/generated/openapi";
import { useScrollLoad } from "@hooks/useScrollLoad";
import Section from "@components/Section";
import type { Status } from "@models/common";
import { UIUploadedVideoCard } from "./UIUploadedVideoCard";

interface UIUploadedVideoListProps {
  enabled: boolean;
  videoSelection?: UseImageBatchSelectionReturn;
  onItemsChange?: (items: UserAssetV2Record[]) => void;
  onRefreshChange?: (refresh: () => void) => void;
}

const PAGE_SIZE = 20;

const UIUploadedVideoList = ({ enabled, videoSelection, onItemsChange, onRefreshChange }: UIUploadedVideoListProps) => {
  const params = useMemo(() => ({}), []);

  const { items, loading, loadingMore, hasMore, sentinelRef, error, refresh } = useScrollLoad<UserAssetV2Record>({
    enabled,
    pageSize: PAGE_SIZE,
    params,
    fetcher: async ({ pageNo, pageSize }) => {
      const res = await getUserAssetsV2({
        assetType: PathsApiUserAssetsGetParametersQueryAssetType.VID,
        assetSource: PathsApiUserAssetsGetParametersQueryAssetSource.USER,
        pageNo,
        pageSize,
      });
      return {
        data: res?.data ?? [],
        pageNo: res?.pageNo ?? pageNo,
        pageSize: res?.pageSize ?? pageSize,
        total: res?.total ?? 0,
      };
    },
  });

  useEffect(() => {
    onItemsChange?.(items);
  }, [items, onItemsChange]);

  useEffect(() => {
    onRefreshChange?.(refresh);
  }, [refresh, onRefreshChange]);

  const handleDelete = async (record: UserAssetV2Record) => {
    if (record.id === null || record.id === undefined) return;
    try {
      await moveToTrash({ id: record.id });
      refresh();
    } catch {
      // HTTP error handler shows toast globally
    }
  };

  const status: Status = useMemo(() => {
    if (loading) return "loading";
    if (error) return "failed";
    if (items.length === 0) return "blank";
    return "succeeded";
  }, [loading, error, items.length]);

  return (
    <Section className="min-h-[300px]" status={status} reload={refresh}>
      <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
        {items.map((item) => (
          <UIUploadedVideoCard
            key={item.id}
            record={item}
            onDelete={handleDelete}
            selected={videoSelection?.isSelected(item.id)}
            onSelectedChange={(sel) => videoSelection?.setSelected(item.id, sel)}
          />
        ))}
      </div>

      <div ref={sentinelRef} className="h-[24px]" />

      {loadingMore && (
        <div className="flex justify-center py-[8px]">
          <Spin size="small" />
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div className="py-[8px] text-center text-[12px] text-kds-fg-soft">
          <Trans>No more items</Trans>
        </div>
      )}
    </Section>
  );
};

export default UIUploadedVideoList;
